import React, { useState, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FileSpreadsheet, DollarSign, RefreshCw, Upload, Settings, X, ArrowRight, Save } from 'lucide-react';
import Modal from '../components/Modal';
import ExportFilterModal from '../components/ExportFilterModal';
import EarlyArrivalsModal from '../components/EarlyArrivalsModal';
import { useLanguage } from '../context/LanguageContext';

function normalizeName(str) {
  return (str || '').replace(/[-_.,()]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

function namesPartialMatch(normA, normB) {
  if (!normA || !normB) return false;
  const wordsA = normA.split(' ');
  const wordsB = normB.split(' ');
  const [shorter, longer] = wordsA.length <= wordsB.length ? [wordsA, wordsB] : [wordsB, wordsA];
  if (shorter.length < 2) return false;
  return shorter.every(w => longer.includes(w));
}

function detectFileDuplicates(json) {
  const badgeNames = {};
  for (const row of json) {
    const badge = String(row['User No.'] || '').trim();
    const name = (row['User Name'] || '').trim();
    if (badge && name && !badgeNames[badge]) {
      badgeNames[badge] = name;
    }
  }
  const badges = Object.keys(badgeNames);
  const duplicates = [];
  const flagged = new Set();
  for (let i = 0; i < badges.length; i++) {
    const normI = normalizeName(badgeNames[badges[i]]);
    if (!normI) continue;
    for (let j = i + 1; j < badges.length; j++) {
      const normJ = normalizeName(badgeNames[badges[j]]);
      if (!normJ) continue;
      if (normI === normJ || namesPartialMatch(normI, normJ)) {
        const pairKey = `${badges[i]}_${badges[j]}`;
        if (flagged.has(pairKey)) continue;
        flagged.add(pairKey);
        const defaultKeep = normI.length <= normJ.length ? badges[i] : badges[j];
        duplicates.push({
          badgeA: badges[i], nameA: badgeNames[badges[i]],
          badgeB: badges[j], nameB: badgeNames[badges[j]],
          merge: true, keepBadge: defaultKeep, chosenName: badgeNames[defaultKeep],
        });
      }
    }
  }
  return duplicates;
}

// Duración en horas decimales → "HH:MM:SS". Con los segundos las columnas de días suman
// exactamente el Total de la fila; a minutos, 08:01 + 07:59 se leía como 16:00 cuando el total
// decía otra cosa. Salen del valor guardado, que el cálculo redondea al segundo (ver roundHrs),
// así que lo impreso es exactamente la resta de los punches.
function fmtHrs(val) {
  if (!val || val === 0) return '-';
  const totalSec = Math.round(val * 3600);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function parseRow(row, facility) {
  let dateVal = '', hourVal = '';
  const dateTime = row['Date/Time'] || '';
  if (dateTime) {
    const dt = new Date(dateTime);
    if (!isNaN(dt)) {
      // Usar fecha/hora LOCAL (no UTC) para evitar shift de día en punches nocturnos.
      // toISOString() convierte a UTC: un punch local de 18:05 en UTC-6 se volvería 00:05 del dia siguiente.
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      const hh = String(dt.getHours()).padStart(2, '0');
      const mi = String(dt.getMinutes()).padStart(2, '0');
      const ss = String(dt.getSeconds()).padStart(2, '0');
      dateVal = `${yyyy}-${mm}-${dd}`;
      hourVal = `${hh}:${mi}:${ss}`;
    }
  }
  return {
    badgenum_ts: row['User No.'] || '', user_name: row['User Name'] || '',
    date_ts: dateVal, hour_ts: hourVal, door_name_ts: row['Door Name'] || '',
    action_ts: row.Message || '', door_num_ts: 0, status_ts: 0, facility,
  };
}

export default function Reports() {
  const { t } = useLanguage();
  const [rawData, setRawData] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [facility, setFacility] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingExportTC, setLoadingExportTC] = useState(false);
  const [loadingExportPR, setLoadingExportPR] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportModalType, setExportModalType] = useState('');
  const fileRef = useRef();

  const [missingModalOpen, setMissingModalOpen] = useState(false);
  const [missingEmployees, setMissingEmployees] = useState([]);
  // Gafetes que el archivo trae a nombre de otra persona: el badge existe, pero registrado con otro
  // nombre. El nombre del checador solo viaja en el archivo, asi que este es el unico momento en el
  // que se puede notar — despues, en la base, solo queda el numero.
  const [nameMismatches, setNameMismatches] = useState([]);
  const [savingMissing, setSavingMissing] = useState(false);
  const [doorsModalOpen, setDoorsModalOpen] = useState(false);
  const [unconfiguredDoors, setUnconfiguredDoors] = useState([]);
  const [savingDoors, setSavingDoors] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateCandidates, setDuplicateCandidates] = useState([]);
  const [earlyArrivalsModalOpen, setEarlyArrivalsModalOpen] = useState(false);
  // Employees with their clock-in days, as returned by the scan: what the calculation modal edits.
  const [scanEmployees, setScanEmployees] = useState([]);
  const [pendingCalcRange, setPendingCalcRange] = useState(null);
  // Badges the last imported file brought. The week in the table can hold many others from previous
  // uploads; the preview and every report generated from here cover only the file's people.
  const [reportBadges, setReportBadges] = useState([]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!selectedFacility) {
      Swal.fire({ icon: 'warning', title: t('rep.selectFacilityTitle'), text: t('rep.selectFacilityMsg') });
      e.target.value = '';
      return;
    }
    e.target.value = '';
    try {
      const XLSX = await import('xlsx');
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);
      const fac = selectedFacility;
      setImportResult(null);
      setPreview(null);
      setFacility(fac);

      const fileDups = detectFileDuplicates(json);
      if (fileDups.length > 0) {
        try {
          const empRes = await axios.get('/api/employees/export');
          const existingBadges = new Set(empRes.data.map(e => String(e.badge_num_emp)));
          for (const dup of fileDups) {
            dup.badgeAExists = existingBadges.has(String(dup.badgeA));
            dup.badgeBExists = existingBadges.has(String(dup.badgeB));
            if (!dup.badgeAExists && dup.badgeBExists) {
              dup.keepBadge = dup.badgeB;
              dup.chosenName = dup.nameB;
            } else if (dup.badgeAExists && !dup.badgeBExists) {
              dup.keepBadge = dup.badgeA;
              dup.chosenName = dup.nameA;
            }
          }
        } catch (err) { /* continue */ }
        setRawData(json);
        setDuplicateCandidates(fileDups);
        setDuplicateModalOpen(true);
        return;
      }

      setRawData(json);
      Swal.fire({ icon: 'info', title: t('rep.fileLoaded'), text: `${json.length} ${t('rep.fileLoadedMsg')}`, timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('common.fileReadError') });
    }
  };

  // paidInBadges: the employees the modal actually asked about. The calculation rebuilds the whole
  // week either way, so without this the times typed for everybody else would be wiped.
  const runCalculateAndPreview = async (df, dt, fac, paidIn, paidInBadges, paidOut, lunch) => {
    Swal.fire({
      title: t('rep.calculating'), html: t('rep.calculating'),
      allowOutsideClick: false, allowEscapeKey: false, showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      const calcRes = await axios.post('/api/timecards/calculate',
        { dateFrom: df, dateTo: dt, facility: fac, paidIn, paidInBadges, paidOut, lunch }, { timeout: 300000 });
      // The calculation rebuilds the whole week, but the preview only shows the file's employees.
      const previewParams = { dateFrom: df, dateTo: dt, facility: fac };
      if (paidInBadges && paidInBadges.length > 0) previewParams.badges = paidInBadges.join(',');
      const prev = await axios.get('/api/timecards/preview', { params: previewParams, timeout: 300000 });
      setPreview(prev.data);
      // Los avisos del calculo — un badge que cobro horas sin estar dado de alta, por ejemplo — se
      // estaban tirando: este camino siempre remataba con el mismo "listo" en verde, pasara lo que
      // pasara. Si hay algo que decir, se dice, y el aviso se queda hasta que se cierre.
      const warnings = calcRes.data?.warnings || [];
      if (warnings.length > 0) {
        await Swal.fire({
          icon: 'warning',
          title: t('rep.importComplete'),
          html: '<span style="color:#ef4444;font-weight:700">' + warnings.join('<br>') + '</span>',
        });
      } else {
        Swal.fire({ icon: 'success', title: t('rep.importComplete'), timer: 2000, showConfirmButton: false });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('rep.processError'), text: err.response?.data?.error || t('rep.unexpectedError') });
    }
  };

  // badges: when the calculation follows an import, only the employees the FILE brought are asked
  // about — the week may hold many others from previous uploads, and being asked about all of them
  // after importing one person is just noise.
  const promptEarlyThenCalculate = async (df, dt, fac, badges) => {
    Swal.fire({
      title: t('early.scanning'), html: t('early.scanning'),
      allowOutsideClick: false, allowEscapeKey: false, showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      const scan = await axios.post('/api/timecards/scan-early-arrivals',
        { dateFrom: df, dateTo: dt, facility: fac, badges });
      const empList = scan.data.employees || [];
      if (empList.length === 0) {
        // Nothing was clocked in in this range: there is no In, Out or lunch to decide, so
        // calculate straight away.
        await runCalculateAndPreview(df, dt, fac, {}, badges, {}, {});
      } else {
        Swal.close();
        setScanEmployees(empList);
        setPendingCalcRange({ dateFrom: df, dateTo: dt, facility: fac, badges });
        setEarlyArrivalsModalOpen(true);
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('rep.unexpectedError') });
    }
  };

  const handleEarlyArrivalsConfirm = async ({ paidIn, paidOut, lunch, badges }) => {
    const range = pendingCalcRange;
    setEarlyArrivalsModalOpen(false);
    setScanEmployees([]);
    setPendingCalcRange(null);
    // Los badges que el modal devuelve son los que quedaron MARCADOS, siempre un subconjunto de los
    // que trajo el archivo. Mandan sobre `range.badges`: si alguien se desmarco, su semana no se
    // toca. Si el modal no los mandara (lote vacio), se cae a los del archivo como siempre.
    if (range) {
      await runCalculateAndPreview(range.dateFrom, range.dateTo, range.facility,
        paidIn, (badges && badges.length > 0) ? badges : range.badges, paidOut, lunch);
    }
  };

  // `ackBadges`: los conflictos de nombre que ya se revisaron en la pantalla. Viajan de vuelta para
  // que el mismo archivo no se vuelva a detener por ellos.
  const doImportAndCalculate = async (dataOverride, ackBadges) => {
    const useData = dataOverride || rawData;
    Swal.fire({
      title: t('rep.saving'), html: t('rep.savingMsg'),
      allowOutsideClick: false, allowEscapeKey: false, showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      const timestamps = useData.map(row => parseRow(row, facility));
      const res = await axios.post('/api/timecards/import-timestamps',
        { timestamps, facility, ackMismatches: ackBadges || [] }, { timeout: 300000 });

      if (res.data.needsDoors) {
        Swal.close();
        setUnconfiguredDoors(res.data.unconfiguredDoors.map(d => ({ ...d, type: d.suggestedType || '' })));
        setDoorsModalOpen(true);
        return;
      }

      if (res.data.needsEmployees) {
        Swal.close();
        const deptOf = (fac) => ((fac || '').toUpperCase().includes('MIA') ? 'TMS MIA'
          : (fac || '').toUpperCase().includes('AUS') ? 'TMS AUSWH' : '');
        setMissingEmployees((res.data.missingEmployees || []).map(emp => ({
          ...emp, type_emp: '',
          department_emp: deptOf(emp.facility),
          company_emp: '', shift_start: '07:00', shift_end: '15:30',
          // `sameBadge`: la misma persona con el MISMO gafete, dada de alta en la otra planta. No
          // hay punches que reasignar, asi que en vez de la casilla "es el mismo" se elige que
          // hacer con su ficha: copiarla a esta planta (default, trabaja en las dos) o moverla
          // (se transfirio). 'new' la da de alta desde cero, como cualquier otro faltante.
          ...(emp.sameBadge ? { sameBadgeAction: 'copy' } : {}),
        })));
        // El gafete ya existe a nombre de alguien mas, asi que el badge del archivo NO se puede
        // reutilizar: quien se de de alta aqui necesita uno nuevo, y por eso arranca vacio.
        setNameMismatches((res.data.nameMismatches || []).map(m => ({
          ...m,
          // Si la persona ya esta dada de alta con otro badge, lo que falta es reasignarle sus
          // punches, no volver a registrarla: esa opcion viene elegida.
          action: m.existingMatch ? 'remap' : 'ignore',
          newBadge: '', type_emp: '',
          department_emp: deptOf(m.facility),
          company_emp: '', shift_start: '07:00', shift_end: '15:30',
        })));
        setMissingModalOpen(true);
        return;
      }

      setImportResult(res.data);
      setRawData(null);

      if (res.data.dateFrom && res.data.dateTo) {
        setDateFrom(res.data.dateFrom);
        setDateTo(res.data.dateTo);
        // Only the employees this file brought get asked about — the week can hold many others from
        // previous uploads, and they keep whatever was decided for them.
        // Los badges a los que la importacion reasigno punches van junto con los del archivo: el
        // archivo trae el gafete viejo, asi que sin esto la persona que se acaba de dar de alta
        // quedaria fuera de la revision de horas y del calculo — que es exactamente lo que se
        // estaba arreglando al darla de alta.
        const fileBadges = [...new Set([
          ...timestamps.map(ts => String(ts.badgenum_ts || '').trim()),
          ...(res.data.aliasBadges || []).map(b => String(b).trim()),
        ].filter(Boolean))];
        setReportBadges(fileBadges);
        await promptEarlyThenCalculate(res.data.dateFrom, res.data.dateTo, facility, fileBadges);
      } else {
        Swal.fire({ icon: 'warning', title: t('rep.noDateTitle'), text: t('rep.noDateMsg') });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('rep.processError'), text: err.response?.data?.error || t('rep.unexpectedError') });
    }
  };

  const handleImport = async () => {
    if (!rawData || rawData.length === 0) return;
    setLoading(true);
    await doImportAndCalculate();
    setLoading(false);
  };

  const handleRecalculate = async () => {
    if (!dateFrom || !dateTo) return;
    // Same scope as the import that produced this preview: recalculating must not pull in the rest
    // of the week's employees.
    await promptEarlyThenCalculate(dateFrom, dateTo, facility, reportBadges);
  };

  const handleSaveDoors = async () => {
    const noType = unconfiguredDoors.filter(d => !d.type);
    if (noType.length > 0) {
      Swal.fire({ icon: 'warning', title: t('rep.typeRequired'), text: t('rep.typeRequiredMsg') });
      return;
    }
    setSavingDoors(true);
    try {
      await axios.post('/api/doors/bulk-create', {
        doors: unconfiguredDoors.map(d => ({ name: d.name, type: d.type, facility: selectedFacility }))
      });
      setDoorsModalOpen(false);
      setUnconfiguredDoors([]);
      await doImportAndCalculate();
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('rep.configError') });
    }
    setSavingDoors(false);
  };

  const updateDoor = (index, field, value) => {
    setUnconfiguredDoors(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSaveMissing = async () => {
    // Mismo gafete, otra planta: no se reasignan punches (irian de 69 a 69, o sea a ningun lado).
    // O se copia su ficha a esta planta, o se mueve la que ya tiene. 'new' cae en el alta normal.
    const sameBadgeRows = missingEmployees.filter(e => e.sameBadge && e.potentialMatch);
    const toCopy = sameBadgeRows.filter(e => e.sameBadgeAction === 'copy');
    const toMove = sameBadgeRows.filter(e => e.sameBadgeAction === 'move');
    // El alta normal: los que no tienen ficha que copiar ni mover, y los que se marcaron 'new'.
    const toCreate = missingEmployees.filter(e =>
      !e.useExisting && !(e.sameBadge && e.sameBadgeAction !== 'new'));
    const toRemap = missingEmployees.filter(e => e.useExisting && e.potentialMatch && !e.sameBadge);
    const incomplete = toCreate.filter(e => !e.type_emp);
    if (incomplete.length > 0) {
      Swal.fire({ icon: 'warning', title: t('rep.typeRequired'), text: t('rep.typeRequiredMsg') });
      return;
    }
    // La ficha que se va a copiar tiene que traer tipo: sin el, el alta se rechaza. Pasa con una
    // ficha vieja capturada sin tipo; se arregla en Empleados, no aqui.
    if (toCopy.some(e => !e.potentialMatch.type_emp)) {
      Swal.fire({ icon: 'warning', title: t('rep.typeRequired'), text: t('rep.typeRequiredMsg') });
      return;
    }
    // Los conflictos de nombre que se decidio dar de alta como persona aparte. El badge nuevo es
    // obligatorio: el del archivo esta ocupado por el empleado que ya existe.
    const mismatchToCreate = nameMismatches.filter(m => m.action === 'register');
    if (mismatchToCreate.some(m => !String(m.newBadge || '').trim())) {
      Swal.fire({ icon: 'warning', title: t('rep.newBadgeRequired'), text: t('rep.newBadgeRequiredMsg') });
      return;
    }
    if (mismatchToCreate.some(m => !m.type_emp)) {
      Swal.fire({ icon: 'warning', title: t('rep.typeRequired'), text: t('rep.typeRequiredMsg') });
      return;
    }
    setSavingMissing(true);
    try {
      if (toCreate.length > 0) {
        await axios.post('/api/employees/bulk-create', { employees: toCreate });
      }
      // Copiar la ficha a esta planta: mismo gafete, mismos datos, otra location. Es lo que hace
      // que el gafete exista en `badge_facility` y que la importacion deje de pedirlo.
      if (toCopy.length > 0) {
        try {
          await axios.post('/api/employees/bulk-create', {
            employees: toCopy.map(e => ({
              badge: e.badge,
              firstName: e.potentialMatch.firstName || e.firstName,
              lastName: e.potentialMatch.lastName || e.lastName,
              facility: e.facility,
              type_emp: e.potentialMatch.type_emp,
              department_emp: e.potentialMatch.department_emp,
              company_emp: e.potentialMatch.company_emp,
              shift_start: e.potentialMatch.shift_start || e.shift_start,
              shift_end: e.potentialMatch.shift_end || e.shift_end,
            })),
          });
        } catch (err) {
          // Ya existia (se copio en un intento anterior del mismo archivo): no es un error.
          if (err.response?.data?.error !== 'DUPLICATE_BADGE') throw err;
        }
      }
      // Moverla: la persona se transfirio de planta. Su ficha es la misma, solo cambia de casa.
      // Las semanas ya calculadas en la planta vieja se quedan donde estan.
      for (const e of toMove) {
        await axios.put(`/api/employees/${e.potentialMatch.id_emp}`, {
          name_emp: e.potentialMatch.firstName || e.firstName,
          last_name_emp: e.potentialMatch.lastName || e.lastName,
          badge_num_emp: e.potentialMatch.badge,
          department_emp: e.potentialMatch.department_emp,
          company_emp: e.potentialMatch.company_emp,
          location_emp: e.facility,
          type_emp: e.potentialMatch.type_emp,
          status_emp: 'Active',
          shift_start: e.potentialMatch.shift_start || e.shift_start,
          shift_end: e.potentialMatch.shift_end || e.shift_end,
          reason: `Transferencia a ${e.facility} desde la importacion de reportes.`,
        });
      }
      if (mismatchToCreate.length > 0) {
        try {
          await axios.post('/api/employees/bulk-create', {
            employees: mismatchToCreate.map(m => ({
              badge: String(m.newBadge).trim(),
              firstName: m.firstName, lastName: m.lastName, facility: m.facility,
              type_emp: m.type_emp, department_emp: m.department_emp, company_emp: m.company_emp,
              shift_start: m.shift_start, shift_end: m.shift_end,
            })),
          });
        } catch (err) {
          // El badge nuevo ya existe: pasa al reimportar el mismo archivo, porque la persona se dio
          // de alta en la pasada anterior. No es un error — lo que falta entonces es solo
          // reasignarle los punches, que es justo lo que sigue.
          if (err.response?.data?.error !== 'DUPLICATE_BADGE') throw err;
        }
      }
      // Las decisiones sobre gafetes a nombre de otra persona se guardan ANTES de reimportar: de
      // ahi en adelante la importacion las aplica sola. Es lo que reasigna los punches al badge
      // nuevo — sin eso, dar de alta a alguien no servia de nada: se quedaba con cero punches, no
      // salia en el calculo, y sus horas se las seguia llevando el empleado que tiene ese gafete.
      // Y es tambien lo que hace que el mismo archivo no vuelva a preguntar lo mismo cada vez.
      if (nameMismatches.length > 0) {
        await axios.post('/api/timecards/badge-aliases', {
          aliases: nameMismatches.map(m => ({
            badge: m.badge, facility: m.facility, fileName: m.fileName,
            // A donde van sus punches: al badge con el que ya esta dada de alta, o al que se le
            // acaba de asignar. "Dejarlo asi" no reasigna nada — solo deja de preguntar.
            targetBadge: m.action === 'remap' ? m.existingMatch?.badge
              : m.action === 'register' ? String(m.newBadge).trim()
                : null,
          })),
        });
      }
      let remappedData = rawData;
      if (toRemap.length > 0 && rawData) {
        const remapMap = {};
        for (const emp of toRemap) { remapMap[String(emp.badge)] = emp.potentialMatch.badge; }
        remappedData = rawData.map(row => {
          const userNo = String(row['User No.'] || '');
          if (remapMap[userNo] !== undefined) return { ...row, 'User No.': remapMap[userNo] };
          return row;
        });
        setRawData(remappedData);
      }
      setMissingModalOpen(false);
      setMissingEmployees([]);
      // Todos los conflictos mostrados quedan por revisados — se hayan dado de alta o se hayan
      // dejado pasar. Si no, el mismo archivo se volveria a detener por ellos en el reintento.
      const ackBadges = nameMismatches.map(m => String(m.badge));
      setNameMismatches([]);
      await doImportAndCalculate(remappedData, ackBadges);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('rep.createError') });
    }
    setSavingMissing(false);
  };

  const handleResolveDuplicates = async () => {
    const toMerge = duplicateCandidates.filter(d => d.merge);
    let remappedData = rawData;
    if (toMerge.length > 0) {
      let existingBadges = new Set();
      try {
        const empRes = await axios.get('/api/employees/export');
        existingBadges = new Set(empRes.data.map(e => String(e.badge_num_emp)));
      } catch (err) { /* continue */ }
      const remapMap = {};
      const nameOverrides = {};
      for (const dup of toMerge) {
        let keepBadge = dup.keepBadge;
        let removeBadge = keepBadge === dup.badgeA ? dup.badgeB : dup.badgeA;
        if (!existingBadges.has(String(keepBadge)) && existingBadges.has(String(removeBadge))) {
          const temp = keepBadge; keepBadge = removeBadge; removeBadge = temp;
        }
        remapMap[String(removeBadge)] = keepBadge;
        nameOverrides[String(keepBadge)] = dup.chosenName;
      }
      remappedData = rawData.map(row => {
        const userNo = String(row['User No.'] || '');
        if (remapMap[userNo] !== undefined) {
          return { ...row, 'User No.': remapMap[userNo], 'User Name': nameOverrides[String(remapMap[userNo])] || row['User Name'] };
        }
        if (nameOverrides[userNo]) return { ...row, 'User Name': nameOverrides[userNo] };
        return row;
      });
      setRawData(remappedData);
    }
    setDuplicateModalOpen(false);
    setDuplicateCandidates([]);
    Swal.fire({ icon: 'info', title: t('rep.fileReady'), text: `${rawData.length} ${t('rep.fileReadyMsg')}`, timer: 2000, showConfirmButton: false });
  };

  const updateMissingEmployee = (index, field, value) => {
    setMissingEmployees(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateMismatch = (index, field, value) => {
    setNameMismatches(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const openExportModal = (type) => {
    setExportModalType(type);
    setExportModalOpen(true);
  };

  const handleExport = async (type, filters = {}) => {
    // Normalmente es la del archivo importado, pero si se pidio el reporte de UNA persona el modal
    // resuelve la facilidad a partir de ella. Respetarla evita pedir un badge de AUSWH con la
    // facilidad en MIA y bajar un reporte vacio.
    const fac = filters.facility || facility;
    const setLoadFn = type === 'timecards' ? setLoadingExportTC : setLoadingExportPR;
    setLoadFn(true);
    try {
      const extraParams = {};
      // The report covers the imported file's employees, nothing else. The modal's own filters
      // (type, badge, company) narrow further inside that set.
      if (reportBadges.length > 0) extraParams.badges = reportBadges.join(',');
      if (filters.employeeType) extraParams.empType = filters.employeeType;
      if (filters.badgeNumber) extraParams.badge = filters.badgeNumber;
      if (filters.company) extraParams.company = filters.company;
      const suffixParts = [];
      if (filters.employeeType) suffixParts.push(filters.employeeType);
      else if (filters.badgeNumber) suffixParts.push(`Badge${filters.badgeNumber}`);
      if (filters.company) suffixParts.push(filters.company.replace(/[^\w-]+/g, '_'));
      const filterSuffix = suffixParts.length ? `_${suffixParts.join('_')}` : '';
      const isExcel = filters.format === 'excel';
      const ext = isExcel ? 'xlsx' : 'pdf';
      const mime = isExcel
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';

      if (type === 'timecards') {
        const endpoint = isExcel ? '/api/timecards/export-timecard-excel' : '/api/timecards/export-timecard';
        const res = await axios.get(endpoint, {
          params: { dateFrom, dateTo, facility: fac, ...extraParams },
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Timecard_${fac || 'ALL'}${filterSuffix}_${dateFrom}_${dateTo}.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        Swal.fire({ icon: 'success', title: t('common.exported'), text: `Timecards ${t('rep.downloadSuccess')}`, timer: 2000, showConfirmButton: false });
      } else {
        let endpoint;
        if (filters.reportVariant === 'summary') {
          endpoint = isExcel ? '/api/timecards/export-payroll-summary-excel' : '/api/timecards/export-payroll-summary-pdf';
        } else {
          endpoint = isExcel ? '/api/timecards/export-payroll-excel' : '/api/timecards/export-payroll-pdf';
        }
        const res = await axios.get(endpoint, {
          params: { dateFrom, dateTo, facility: fac, ...extraParams },
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
        const link = document.createElement('a');
        link.href = url;
        const label = filters.reportVariant === 'summary' ? 'PAYROLL TOTAL HOURS' : 'PAYROLL SUMMARY';
        link.setAttribute('download', `${label} ${fac || 'ALL'}${filterSuffix} week ending ${dateTo.replace(/-/g, '.')}.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        Swal.fire({ icon: 'success', title: t('common.exported'), text: `Payroll ${isExcel ? 'Excel' : 'PDF'} ${t('rep.downloadSuccess')}`, timer: 2000, showConfirmButton: false });
      }
      setExportModalOpen(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('rep.exportError'), text: t('rep.exportErrorMsg') });
    }
    setLoadFn(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t('rep.title')}</h1>
      </div>

      {/* Facility Selector — hidden once results are shown (badge in preview header still indicates facility) */}
      {!(preview && preview.length > 0) && (
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span className="card-title">{t('rep.selectFacility')}</span>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { value: 'MIA', label: t('rep.miami'), icon: 'MIA' },
            { value: 'AUSWH', label: t('rep.austin'), icon: 'AUS' },
          ].map(opt => (
            <div key={opt.value}
              className={`facility-card ${selectedFacility === opt.value ? 'facility-card-active' : ''}`}
              onClick={() => { setSelectedFacility(opt.value); setFacility(opt.value); setRawData(null); setImportResult(null); setPreview(null); setDateFrom(''); setDateTo(''); setReportBadges([]); }}>
              <div className="facility-card-icon">{opt.icon}</div>
              <div className="facility-card-label">{opt.label}</div>
            </div>
          ))}
        </div>
      </div>
      )}

      {!(preview && preview.length > 0) && (
      <div className="card" style={{ marginBottom: 24, opacity: selectedFacility ? 1 : 0.5, pointerEvents: selectedFacility ? 'auto' : 'none' }}>
        {/* Header and drop zone both step aside once a file is loaded: the record count IS the
            title from then on, and picking another file is one button away below. */}
        {!rawData && (
          <div className="card-header">
            <span className="card-title">{t('rep.importFile')}</span>
            {selectedFacility && <span className="badge badge-info" style={{ fontSize: 12 }}>{selectedFacility}</span>}
          </div>
        )}
        {!rawData && (
          <div className="file-upload" onClick={() => selectedFacility && fileRef.current?.click()}>
            <div className="file-upload-icon">{'\u21E7'}</div>
            <div className="file-upload-text">
              <strong>{selectedFacility ? t('rep.clickUpload') : t('rep.selectFirst')}</strong><br />
              {t('rep.xlsFormat')}
            </div>
          </div>
        )}
        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileUpload} />

        {rawData && (
          <div>
            {/* With the header hidden this line is the title of the card. */}
            <p style={{ color: 'var(--text-secondary)', marginBottom: 10, fontSize: 15 }}>
              <strong>{rawData.length}</strong> {t('rep.records')} <strong>{facility}</strong>
            </p>
            <div className="table-wrapper" style={{ maxHeight: 180, overflowY: 'auto' }}>
              <table>
                <thead><tr><th>{t('raw.dateTime')}</th><th>{t('raw.userNo')}</th><th>{t('raw.userName')}</th><th>{t('raw.doorName')}</th><th>{t('raw.message')}</th></tr></thead>
                <tbody>
                  {rawData.slice(0, 5).map((row, i) => (
                    <tr key={i}><td>{row['Date/Time']}</td><td>{row['User No.']}</td><td>{row['User Name']}</td><td>{row['Door Name']}</td><td>{row.Message}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }} onClick={handleImport} disabled={loading}>
                <Upload size={15} style={{ marginRight: 6 }} />{loading ? t('rep.processing') : `${t('rep.import')} ${rawData.length} ${t('rep.importRecords')}`}
              </button>
              {/* The way back to the drop zone, now that it is hidden. */}
              <button className="btn-secondary" style={{ width: 'auto', padding: '8px 20px' }}
                onClick={() => fileRef.current?.click()} disabled={loading}>
                {t('rep.changeFile')}
              </button>
            </div>
          </div>
        )}

        {importResult && (
          <div style={{ marginTop: 16, padding: 12, background: 'rgba(34,197,94,0.1)', borderRadius: 'var(--radius)', color: 'var(--success)' }}>
            {importResult.message}
            {importResult.dateFrom && <span> — {t('rep.period')} {importResult.dateFrom} a {importResult.dateTo}</span>}
          </div>
        )}
      </div>
      )}

      {preview && preview.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <span className="card-title">{t('rep.generateReports')}</span>
            <span style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="badge badge-info" style={{ fontSize: 16, fontWeight: 800, padding: '6px 14px', letterSpacing: 0.5 }}>{facility}</span>
              <span>{dateFrom} — {dateTo} | {preview.length} {t('rep.employees')}</span>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <button className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }} onClick={() => openExportModal('timecards')} disabled={loadingExportTC}>
              <FileSpreadsheet size={15} style={{ marginRight: 6 }} />{loadingExportTC ? t('common.exporting') : t('rep.generateTimecards')}
            </button>
            <button className="btn-primary" style={{ width: 'auto', padding: '8px 24px', background: 'var(--warning)' }} onClick={() => openExportModal('payroll')} disabled={loadingExportPR}>
              <DollarSign size={15} style={{ marginRight: 6 }} />{loadingExportPR ? t('common.exporting') : t('rep.generatePayrolls')}
            </button>
            <button className="btn-secondary" style={{ width: 'auto', padding: '8px 24px' }} onClick={handleRecalculate}>
              <RefreshCw size={15} style={{ marginRight: 6 }} />{t('rep.recalculate')}
            </button>
          </div>
          <div className="table-wrapper" style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>{t('common.badge')}</th><th>{t('hrs.facility')}</th><th>{t('hrs.employee')}</th>
                  <th>{t('day.mon')}</th><th>{t('day.tue')}</th><th>{t('day.wed')}</th><th>{t('day.thu')}</th><th>{t('day.fri')}</th><th>{t('day.sat')}</th><th>{t('day.sun')}</th>
                  <th>{t('hrs.total')}</th><th>{t('hrs.regular')}</th><th>{t('hrs.overtime')}</th><th>{t('hrs.paidAbs')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i}>
                    <td>{r.badgenum_tc}</td>
                    <td><span className="badge badge-info">{r.facility_tc}</span></td>
                    <td>{r.name_emp} {r.last_name_emp}</td>
                    <td>{fmtHrs(r.mon_hrs)}</td><td>{fmtHrs(r.tue_hrs)}</td><td>{fmtHrs(r.wed_hrs)}</td><td>{fmtHrs(r.thu_hrs)}</td><td>{fmtHrs(r.fri_hrs)}</td><td>{fmtHrs(r.sat_hrs)}</td><td>{fmtHrs(r.sun_hrs)}</td>
                    <td><strong style={{ color: 'var(--accent)' }}>{fmtHrs(r.total_hrs)}</strong></td>
                    <td>{fmtHrs(r.regular_hrs)}</td>
                    <td style={{ color: r.overtime_hrs > 0 ? 'var(--warning)' : 'inherit' }}>{fmtHrs(r.overtime_hrs)}</td>
                    <td>{fmtHrs(r.paid_abs_hrs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {preview && preview.length === 0 && (
        <div className="card">
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            {t('rep.noHours')} {dateFrom} a {dateTo}. {t('rep.checkDoors')}
          </p>
        </div>
      )}

      {/* Modal: Unconfigured Doors */}
      <Modal isOpen={doorsModalOpen} onClose={() => setDoorsModalOpen(false)} title={`${t('rep.configureDoors')} (${unconfiguredDoors.length})`} large>
        <div className="modal-body">
          <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14 }}>{t('rep.configureDoorsDesc')}</p>
          <table>
            <thead><tr><th>{t('rep.doorName')}</th><th>{t('emp.typeReq')}</th></tr></thead>
            <tbody>
              {unconfiguredDoors.map((door, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{door.name}</td>
                  <td>
                    <select className="form-input" style={{ padding: '4px 8px', fontSize: 13, borderColor: !door.type ? 'var(--danger)' : undefined }} value={door.type} onChange={e => updateDoor(i, 'type', e.target.value)}>
                      <option value="">{t('common.select')}</option>
                      <option value="Clock In">{t('door.clockIn')}</option>
                      <option value="Clock In (No Count)">{t('door.clockInNoCount')}</option>
                      <option value="Clock Out">{t('door.clockOut')}</option>
                      <option value="Lunch">{t('door.lunch')}</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={() => setDoorsModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
          <button type="button" className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }} onClick={handleSaveDoors} disabled={savingDoors}>
            <Settings size={15} style={{ marginRight: 6 }} />{savingDoors ? t('common.saving') : t('rep.configAndContinue')}
          </button>
        </div>
      </Modal>

      {/* Modal: Duplicate Employees in File */}
      <Modal isOpen={duplicateModalOpen} onClose={() => setDuplicateModalOpen(false)} title={`${t('rep.fileDups')} (${duplicateCandidates.length})`} large>
        <div className="modal-body">
          <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14 }}>{t('rep.fileDupsDesc')}</p>
          {duplicateCandidates.map((dup, i) => (
            <div key={i} style={{ border: '1px solid var(--warning)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 12, background: 'var(--bg-tertiary)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', marginBottom: 12 }}>
                <input type="checkbox" checked={dup.merge} onChange={e => { setDuplicateCandidates(prev => { const updated = [...prev]; updated[i] = { ...updated[i], merge: e.target.checked }; return updated; }); }} style={{ width: 16, height: 16 }} />
                <span style={{ fontWeight: 700 }}>{t('rep.samePerson')}</span>
              </label>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                  {dup.merge ? t('emp.badgeKeep') : t('rep.detectedBadges')}
                </span>
                {[{ badge: dup.badgeA, name: dup.nameA, exists: dup.badgeAExists }, { badge: dup.badgeB, name: dup.nameB, exists: dup.badgeBExists }].map(emp => (
                  <label key={emp.badge} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 4, borderRadius: 'var(--radius)', cursor: dup.merge ? 'pointer' : 'default', border: dup.merge && dup.keepBadge === emp.badge ? '2px solid var(--success)' : '1px solid var(--border)', background: dup.merge && dup.keepBadge === emp.badge ? 'rgba(34,197,94,0.08)' : 'transparent' }}>
                    {dup.merge && <input type="radio" name={`keep_${i}`} checked={dup.keepBadge === emp.badge} onChange={() => { setDuplicateCandidates(prev => { const updated = [...prev]; updated[i] = { ...updated[i], keepBadge: emp.badge }; return updated; }); }} />}
                    <span style={{ fontWeight: 700, minWidth: 80 }}>Badge {emp.badge}</span>
                    <span>{emp.name}</span>
                    {emp.exists && <span className="badge badge-success" style={{ fontSize: 10, padding: '2px 6px' }}>{t('rep.inSystem')}</span>}
                    {dup.merge && dup.keepBadge === emp.badge && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)', marginLeft: 'auto' }}>{t('emp.keep')}</span>}
                    {dup.merge && dup.keepBadge !== emp.badge && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)', marginLeft: 'auto' }}>{t('rep.mergeLabel')}</span>}
                  </label>
                ))}
              </div>
              {dup.merge && (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>{t('emp.correctName')}</span>
                    {[{ badge: dup.badgeA, name: dup.nameA }, { badge: dup.badgeB, name: dup.nameB }].map(emp => (
                      <label key={emp.badge} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 4, borderRadius: 'var(--radius)', cursor: 'pointer', border: dup.chosenName === emp.name ? '2px solid var(--primary)' : '1px solid var(--border)', background: dup.chosenName === emp.name ? 'rgba(59,130,246,0.08)' : 'transparent' }}>
                        <input type="radio" name={`name_${i}`} checked={dup.chosenName === emp.name} onChange={() => { setDuplicateCandidates(prev => { const updated = [...prev]; updated[i] = { ...updated[i], chosenName: emp.name }; return updated; }); }} />
                        <span style={{ fontWeight: dup.chosenName === emp.name ? 700 : 400 }}>{emp.name}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>(Badge {emp.badge})</span>
                      </label>
                    ))}
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--warning)' }}>{t('rep.mergeTimestamps')}</p>
                </>
              )}
              {!dup.merge && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -4 }}>{t('rep.separateImport')}</p>}
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={() => setDuplicateModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
          <button type="button" className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }} onClick={handleResolveDuplicates}><ArrowRight size={15} style={{ marginRight: 4 }} />{t('rep.continue')}</button>
        </div>
      </Modal>

      {/* Modal: Missing Employees */}
      <Modal isOpen={missingModalOpen} onClose={() => setMissingModalOpen(false)}
        title={`${t('rep.missingTitle')} (${missingEmployees.length + nameMismatches.length})`} xl>
        <div className="modal-body">
          {/* Gafetes que el archivo trae a nombre de otra persona. Van arriba de los faltantes
              porque son el caso silencioso: el badge existe, la importacion no falla, y las horas
              de quien trae el gafete se le pagan al que esta registrado. */}
          {nameMismatches.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 6px', fontSize: 15, color: 'var(--warning)' }}>
                {t('rep.mismatchTitle')} ({nameMismatches.length})
              </h4>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 12, fontSize: 13.5, lineHeight: 1.5 }}>
                {t('rep.mismatchDesc')}
              </p>
              {nameMismatches.map((m, i) => (
                <div key={`mm-${i}`} style={{ border: '1px solid var(--warning)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 12, background: 'rgba(245,158,11,0.08)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)' }}>Badge {m.badge}</span>
                    <span className="badge badge-info">{m.facility}</span>
                    <span style={{ fontSize: 13 }}>
                      {t('rep.mismatchFile')} <strong>{m.fileName}</strong>
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {t('rep.mismatchRegistered')} <strong>{m.registeredName}</strong>
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
                    {/* La persona del archivo ya esta en Empleados con otro gafete: no hay nada que
                        registrar, solo reasignarle sus punches. */}
                    {m.existingMatch && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                        <input type="radio" name={`mm-${i}`} checked={m.action === 'remap'}
                          onChange={() => updateMismatch(i, 'action', 'remap')} />
                        <span>
                          {t('rep.mismatchExisting')} <strong>Badge {m.existingMatch.badge}</strong>
                          {' — '}{m.existingMatch.name}
                          {m.existingMatch.facility !== m.facility && (
                            <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 10 }}>{m.existingMatch.facility}</span>
                          )}
                        </span>
                      </label>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                      <input type="radio" name={`mm-${i}`} checked={m.action === 'ignore'}
                        onChange={() => updateMismatch(i, 'action', 'ignore')} />
                      {t('rep.mismatchIgnore')}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                      <input type="radio" name={`mm-${i}`} checked={m.action === 'register'}
                        onChange={() => updateMismatch(i, 'action', 'register')} />
                      {t('rep.mismatchRegister')}
                    </label>
                  </div>
                  {m.action === 'remap' && (
                    <p style={{ margin: '0 0 4px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      {t('rep.mismatchRemapHint')}
                    </p>
                  )}
                  {m.action === 'register' && (
                    <>
                      <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        {t('rep.mismatchRegisterHint')}
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                        <div>
                          <label className="form-label" style={{ fontSize: 12, color: !String(m.newBadge || '').trim() ? 'var(--danger)' : undefined }}>
                            {t('rep.mismatchNewBadge')}
                          </label>
                          <input className="form-input" value={m.newBadge}
                            style={{ borderColor: !String(m.newBadge || '').trim() ? 'var(--danger)' : undefined }}
                            onChange={e => updateMismatch(i, 'newBadge', e.target.value)} />
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: 12 }}>{t('common.name')}</label>
                          <input className="form-input" value={m.firstName}
                            onChange={e => updateMismatch(i, 'firstName', e.target.value)} />
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: 12 }}>{t('common.lastName')}</label>
                          <input className="form-input" value={m.lastName}
                            onChange={e => updateMismatch(i, 'lastName', e.target.value)} />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 8 }}>
                        <div>
                          <label className="form-label" style={{ fontSize: 12, color: !m.type_emp ? 'var(--danger)' : undefined }}>{t('emp.typeReq')}</label>
                          <select className="form-input" value={m.type_emp}
                            style={{ borderColor: !m.type_emp ? 'var(--danger)' : undefined }}
                            onChange={e => updateMismatch(i, 'type_emp', e.target.value)}>
                            <option value="">{t('common.select')}</option>
                            <option value="PERM">PERM</option>
                            <option value="PERM-HOUR">PERM-HOUR</option>
                            <option value="PERM-SALARY">PERM-SALARY</option>
                            <option value="TEMP">TEMP</option>
                            <option value="CONTR">CONTR</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: 12 }}>{t('emp.shiftStart')}</label>
                          <input className="form-input" type="time" value={m.shift_start}
                            onChange={e => updateMismatch(i, 'shift_start', e.target.value)} />
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: 12 }}>{t('emp.shiftEnd')}</label>
                          <input className="form-input" type="time" value={m.shift_end}
                            onChange={e => updateMismatch(i, 'shift_end', e.target.value)} />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          {missingEmployees.length > 0 && (
            <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14 }}>{t('rep.missingDesc')}</p>
          )}
          {missingEmployees.map((emp, i) => {
            // La ficha sale de la que ya existe (marcada "es el mismo", copiada o movida): los
            // campos de abajo son entonces informativos y no viajan a ningun lado.
            const usingExisting = !!emp.useExisting
              || (!!emp.sameBadge && emp.sameBadgeAction !== 'new');
            const pm = emp.potentialMatch;
            return (
            <div key={i} style={{ border: emp.potentialMatch ? '1px solid var(--warning)' : '1px solid var(--border-color)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 12, background: 'var(--bg-tertiary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)' }}>Badge {emp.badge}</span>
                <span className="badge badge-info">{emp.facility}</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{emp.firstName} {emp.lastName}</span>
              </div>
              {/* Mismo gafete, otra planta. No se ofrece "es el mismo" porque no hay punches que
                  reasignar (irian de 69 a 69): se decide que hacer con su FICHA. */}
              {emp.potentialMatch && emp.sameBadge && (
                <div style={{ marginBottom: 12, padding: 12, background: 'rgba(245,158,11,0.1)', borderRadius: 'var(--radius)', border: '1px solid var(--warning)' }}>
                  <div style={{ fontSize: 13, marginBottom: 8 }}>
                    {t('rep.sameBadgeIntro')} <strong>Badge {emp.potentialMatch.badge}</strong> ({emp.potentialMatch.name})
                    <span className="badge badge-info" style={{ marginLeft: 6 }}>{emp.potentialMatch.location}</span>
                    <span className="badge badge-warning" style={{ marginLeft: 6 }}>{emp.potentialMatch.type_emp}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="radio" name={`sb-${i}`} checked={emp.sameBadgeAction === 'copy'}
                        onChange={() => updateMissingEmployee(i, 'sameBadgeAction', 'copy')} />
                      <span>{t('rep.sameBadgeCopy').replace('{fac}', emp.facility)}</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="radio" name={`sb-${i}`} checked={emp.sameBadgeAction === 'move'}
                        onChange={() => updateMissingEmployee(i, 'sameBadgeAction', 'move')} />
                      <span>{t('rep.sameBadgeMove').replace('{from}', emp.potentialMatch.location || '—').replace('{to}', emp.facility)}</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="radio" name={`sb-${i}`} checked={emp.sameBadgeAction === 'new'}
                        onChange={() => updateMissingEmployee(i, 'sameBadgeAction', 'new')} />
                      <span>{t('rep.sameBadgeNew')}</span>
                    </label>
                  </div>
                  <p style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                    {emp.sameBadgeAction === 'move' ? t('rep.sameBadgeMoveHint')
                      : emp.sameBadgeAction === 'new' ? t('rep.sameBadgeNewHint')
                        : t('rep.sameBadgeCopyHint')}
                  </p>
                </div>
              )}
              {emp.potentialMatch && !emp.sameBadge && (
                <div style={{ marginBottom: 12, padding: 12, background: 'rgba(245,158,11,0.1)', borderRadius: 'var(--radius)', border: '1px solid var(--warning)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={emp.useExisting || false} onChange={e => updateMissingEmployee(i, 'useExisting', e.target.checked)} style={{ width: 16, height: 16 }} />
                    <span>
                      {t('rep.sameAs')} <strong>Badge {emp.potentialMatch.badge}</strong> ({emp.potentialMatch.name})
                      <span className="badge badge-info" style={{ marginLeft: 6 }}>{emp.potentialMatch.location}</span>
                      <span className="badge badge-warning" style={{ marginLeft: 6 }}>{emp.potentialMatch.type_emp}</span>
                    </span>
                  </label>
                  {emp.useExisting && (
                    <p style={{ marginTop: 8, fontSize: 12, color: 'var(--warning)' }}>
                      {t('rep.timestampsAssign')} {emp.badge} {t('rep.willAssignTo')} {emp.potentialMatch.badge}.
                    </p>
                  )}
                </div>
              )}
              {/* Los campos de alta solo mandan cuando de verdad se va a crear la ficha desde cero:
                  copiar o mover usa los datos de la que ya existe. */}
              <div style={{ opacity: usingExisting ? 0.5 : 1, pointerEvents: usingExisting ? 'none' : 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>{t('common.name')}</label>
                    <input className="form-input" value={usingExisting ? pm?.firstName || pm?.name?.split(' ')[0] || emp.firstName : emp.firstName} onChange={e => updateMissingEmployee(i, 'firstName', e.target.value)} readOnly={usingExisting} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>{t('common.lastName')}</label>
                    <input className="form-input" value={usingExisting ? pm?.lastName || pm?.name?.split(' ').slice(1).join(' ') || emp.lastName : emp.lastName} onChange={e => updateMissingEmployee(i, 'lastName', e.target.value)} readOnly={usingExisting} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: 12, color: !usingExisting && !emp.type_emp ? 'var(--danger)' : undefined }}>{t('emp.typeReq')}</label>
                    <select className="form-input" style={{ borderColor: !usingExisting && !emp.type_emp ? 'var(--danger)' : undefined }} value={usingExisting ? pm?.type_emp || '' : emp.type_emp} onChange={e => updateMissingEmployee(i, 'type_emp', e.target.value)} disabled={usingExisting}>
                      <option value="">{t('common.select')}</option>
                      <option value="PERM">PERM</option>
                      <option value="PERM-HOUR">PERM-HOUR</option>
                      <option value="PERM-SALARY">PERM-SALARY</option>
                      <option value="TEMP">TEMP</option>
                      <option value="CONTR">CONTR</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 8 }}>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>{t('common.location')}</label>
                    {/* Copiar o mover deja la ficha en la planta del ARCHIVO, no en la de origen:
                        es justo lo que estas dos opciones arreglan. Solo "es el mismo" (reasignar
                        punches a otro gafete) se queda con la planta de la ficha que ya existe. */}
                    <input className="form-input" value={emp.useExisting ? pm?.location || emp.facility : emp.facility} readOnly style={{ opacity: 0.7 }} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>{t('common.department')}</label>
                    <select className="form-input" value={usingExisting ? pm?.department_emp || '' : emp.department_emp} onChange={e => updateMissingEmployee(i, 'department_emp', e.target.value)} disabled={usingExisting}>
                      <option value="">{t('common.select')}</option>
                      <option value="TMS MIA">TMS MIA</option>
                      <option value="TMS AUSWH">TMS AUSWH</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>{t('common.company')}</label>
                    <input className="form-input" value={usingExisting ? pm?.company_emp || '' : emp.company_emp} onChange={e => updateMissingEmployee(i, 'company_emp', e.target.value)} readOnly={usingExisting} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 8 }}>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>{t('emp.shiftStart')}</label>
                    <input className="form-input" type="time" value={(usingExisting ? pm?.shift_start : emp.shift_start) || emp.shift_start || '07:00'} onChange={e => updateMissingEmployee(i, 'shift_start', e.target.value)} readOnly={usingExisting} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: 12 }}>{t('emp.shiftEnd')}</label>
                    <input className="form-input" type="time" value={(usingExisting ? pm?.shift_end : emp.shift_end) || emp.shift_end || '15:30'} onChange={e => updateMissingEmployee(i, 'shift_end', e.target.value)} readOnly={usingExisting} />
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={() => setMissingModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
          <button type="button" className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }} onClick={handleSaveMissing} disabled={savingMissing}>
            <Save size={15} style={{ marginRight: 6 }} />{savingMissing ? t('common.saving') : t('rep.saveAndContinue')}
          </button>
        </div>
      </Modal>

      <ExportFilterModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        exportType={exportModalType}
        loading={exportModalType === 'timecards' ? loadingExportTC : loadingExportPR}
        onExport={(filters) => handleExport(exportModalType, filters)}
        defaultFacility={facility}
        // Sin `showDateRange`: aqui la semana la dicta el archivo importado y no se edita. Las
        // fechas viajan igual para que el modal pueda revisar los punches manuales del periodo.
        defaultDateFrom={dateFrom}
        defaultDateTo={dateTo}
      />

      <EarlyArrivalsModal
        isOpen={earlyArrivalsModalOpen}
        onClose={() => { setEarlyArrivalsModalOpen(false); setScanEmployees([]); setPendingCalcRange(null); }}
        employees={scanEmployees}
        onConfirm={handleEarlyArrivalsConfirm}
      />
    </div>
  );
}
